import React, { Suspense } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { CartProvider } from "./Component/CartContext";
import Header from "./Component/Header";
import Footer from "./Component/Footer";
import ScrollToTop from "./Component/ScrollToTop";
import ErrorBoundary from "./Component/ErrorBoundary";
import siteConfig from "./config/siteConfig";

const Main = React.lazy(() => import("./Component/Main"));
const Shop = React.lazy(() => import("./Component/Shop"));
const ProductDetail = React.lazy(() => import("./Component/ProductDetail"));
const CartPage = React.lazy(() => import("./Component/CartPage"));
const CheckOut = React.lazy(() => import("./Component/CheckOut"));
const PaySuccess = React.lazy(() => import("./Component/SuccessPay"));
const About = React.lazy(() => import("./Component/About"));
const Contact = React.lazy(() => import("./Component/Contact"));
const Delivery = React.lazy(() => import("./Component/Delivery"));
const Returns = React.lazy(() => import("./Component/Returns"));
const Terms = React.lazy(() => import("./Component/Terms"));
const Privacy = React.lazy(() => import("./Component/Privacy"));
const Cookies = React.lazy(() => import("./Component/Cookies"));
const Faqs = React.lazy(() => import("./Component/Faqs"));
const Account = React.lazy(() => import("./Component/Account"));
const Wishlist = React.lazy(() => import("./Component/Wishlist"));
const NotFound = React.lazy(() => import("./Component/NotFound"));

function PageLoader() {
  return <div className="page-loader" role="status" aria-label="Loading page"><span /></div>;
}

function Storefront() {
  const { pathname } = useLocation();
  const focusedCheckout = pathname === "/checkout";
  React.useEffect(() => {
    const routeTitles = {
      "/": "Portable tech essentials",
      "/shop": "Shop all",
      "/cartpage": "Shopping bag",
      "/cart": "Shopping bag",
      "/checkout": "Secure checkout",
      "/payment": "Payment status",
      "/about": "Our story",
      "/contact": "Contact us",
      "/shipping": "Shipping & delivery",
      "/returns": "Returns",
      "/privacy": "Privacy policy",
      "/cookies": "Cookie policy",
      "/faqs": "Frequently asked questions",
      "/terms": "Terms of service",
      "/wishlist": "Wishlist",
      "/account": "Account",
    };
    const title = pathname.startsWith("/shopDetails/") || pathname.startsWith("/products/") || pathname.startsWith("/product/")
      ? "Product details"
      : routeTitles[pathname] || "Storefront";
    document.title = `${title} | ${siteConfig.brandName}`;
    const description = document.querySelector('meta[name="description"]');
    description?.setAttribute("content", siteConfig.description);
  }, [pathname]);
  return (
    <>
      <a className="skip-link" href="#main-content">Skip to content</a>
      <ScrollToTop />
      {!focusedCheckout && <Header />}
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<Main />} />
          <Route path="/shop" element={<Shop />} />
          <Route path="/search" element={<Shop />} />
          <Route path="/category/:category" element={<Shop />} />
          <Route path="/shopDetails/:id" element={<ProductDetail />} />
          <Route path="/products/:id" element={<ProductDetail />} />
          <Route path="/product/:id" element={<ProductDetail />} />
          <Route path="/cartpage" element={<CartPage />} />
          <Route path="/cart" element={<CartPage />} />
          <Route path="/checkout" element={<CheckOut />} />
          <Route path="/paysuccess" element={<PaySuccess />} />
          <Route path="/payment" element={<PaySuccess />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/shipping" element={<Delivery />} />
          <Route path="/returns" element={<Returns />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/cookies" element={<Cookies />} />
          <Route path="/faqs" element={<Faqs />} />
          <Route path="/account" element={<Account />} />
          <Route path="/wishlist" element={<Wishlist />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      {!focusedCheckout && <Footer />}
    </>
  );
}

function App() {
  return (
    <CartProvider>
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <ErrorBoundary><Storefront /></ErrorBoundary>
      </BrowserRouter>
    </CartProvider>
  );
}

export default App;
